import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';
import forestRainAudio from '@/assets/forest-rain.mp3';
import oceanWavesAudio from '@/assets/ocean-waves.mp3';
import monkPadAudio from '@/assets/monk-pad.mp3';

export interface ExperienceTheme {
  name: string;
  audio: string;
  backgroundGradient: string;
  countdownMessage: string;
  endMessage: string;
}

export const experienceThemes: ExperienceTheme[] = [
  {
    name: 'forest',
    audio: forestRainAudio,
    backgroundGradient: 'linear-gradient(to bottom, hsl(130, 30%, 15%), hsl(130, 35%, 10%))',
    countdownMessage: "This minute's on us. Don't worry — the internet will wait.",
    endMessage: "Welcome back. Nothing changed — and that's the point.",
  },
  {
    name: 'ocean',
    audio: oceanWavesAudio,
    backgroundGradient: 'linear-gradient(to bottom, hsl(200, 50%, 20%), hsl(210, 60%, 15%))',
    countdownMessage: "A rare corner of the internet that prefers when you leave.",
    endMessage: "Congratulations — you just did absolutely nothing. Perfectly.",
  },
  {
    name: 'storm',
    audio: forestRainAudio,
    backgroundGradient: 'linear-gradient(to bottom, hsl(160, 25%, 22%), hsl(165, 30%, 15%))',
    countdownMessage: "You came online to remember the offline.",
    endMessage: "A minute well wasted.",
  },
  {
    name: 'monk',
    audio: monkPadAudio,
    backgroundGradient: 'linear-gradient(to bottom, hsl(30, 60%, 35%), hsl(25, 55%, 25%))',
    countdownMessage: "Go offline. It's very on-brand.",
    endMessage: "You've officially outsmarted the algorithm.",
  },
];

interface OfflineMinuteExperienceProps {
  isActive: boolean;
  onClose: () => void;
  theme: ExperienceTheme;
}

export const OfflineMinuteExperience = ({ isActive, onClose, theme }: OfflineMinuteExperienceProps) => {
  const [timeLeft, setTimeLeft] = useState(60);
  const [showEndMessage, setShowEndMessage] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Countdown timer
  useEffect(() => {
    if (!isActive || timeLeft === 0) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isActive, timeLeft]);

  // Start/stop audio
  useEffect(() => {
    if (isActive) {
      audioRef.current = new Audio(theme.audio);
      audioRef.current.loop = true;
      audioRef.current.volume = 0.5;
      audioRef.current.play();
      document.body.style.overflow = 'hidden';
    }

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      document.body.style.overflow = 'unset';
    };
  }, [isActive, theme.audio]);

  // Handle timer completion
  useEffect(() => {
    if (timeLeft === 0 && isActive) {
      setShowEndMessage(true);
      
      // Fade out after 5 seconds
      setTimeout(() => {
        onClose();
        setShowEndMessage(false);
        setTimeLeft(60);
      }, 5000);
    }
  }, [timeLeft, isActive, onClose]);

  // Reset timer when activated
  useEffect(() => {
    if (isActive) {
      setTimeLeft(60);
      setShowEndMessage(false);
    }
  }, [isActive]);

  const handleExit = () => {
    onClose();
    setShowEndMessage(false);
    setTimeLeft(60);
  };

  if (!isActive) {
    return null;
  }

  // Calculate circular progress
  const progress = (timeLeft / 60) * 283; // 283 is approximate circumference for r=45

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center animate-fade-in"
      style={{
        background: theme.backgroundGradient,
      }}
    >
      {/* Foggy overlay effect */}
      <div 
        className="absolute inset-0 opacity-40"
        style={{
          backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255,255,255,0.08) 0%, transparent 50%)',
        }}
      />

      {/* Exit button */}
      <button
        onClick={handleExit}
        className="absolute top-8 right-8 z-20 text-white/60 hover:text-white transition-colors"
        aria-label="Exit experience"
      >
        <X className="w-8 h-8" />
      </button>

      {/* Content */}
      <div className="relative z-10 text-center">
        {!showEndMessage ? (
          <>
            {/* Circular timer */}
            <div className="relative w-48 h-48 mx-auto mb-8">
              <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
                {/* Background circle */}
                <circle
                  cx="50"
                  cy="50"
                  r="45"
                  fill="none"
                  stroke="rgba(255,255,255,0.1)"
                  strokeWidth="2"
                />
                {/* Progress circle */}
                <circle
                  cx="50"
                  cy="50"
                  r="45"
                  fill="none"
                  stroke="rgba(255,255,255,0.8)"
                  strokeWidth="2"
                  strokeDasharray="283"
                  strokeDashoffset={283 - progress}
                  strokeLinecap="round"
                  className="transition-all duration-1000 ease-linear"
                />
              </svg>
              {/* Timer text */}
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-6xl font-light text-white">{timeLeft}</span>
              </div>
            </div>

            <p className="text-xl font-light text-white/80 tracking-wide">
              {theme.countdownMessage}
            </p>
          </>
        ) : (
          <div className="animate-fade-in">
            <p className="text-3xl font-light text-white tracking-wide px-8">
              {theme.endMessage}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
